<script setup lang="ts">
import axios from 'axios'
import { Download, Loader2 } from 'lucide-vue-next'

const CUSTOMER_EXPORT_COLUMNS = [
  'id',
  'public_id',
  'phone',
  'last_checkin_date',
  'register_date',
  'birthday',
  'firstname',
  'lastname',
  'email',
  'gender',
  'has_optin_sms',
  'has_optin_email',
  'audiences',
  'points',
  'checkin_count',
  'store',
  'city',
  'postal_code',
  'old_loyalty_card_number'
] as const

type CustomerExportColumn = (typeof CUSTOMER_EXPORT_COLUMNS)[number]

const DEFAULT_SELECTED_COLUMNS: CustomerExportColumn[] = [
  'public_id',
  'phone',
  'last_checkin_date',
  'register_date',
  'birthday',
  'firstname',
  'lastname',
  'email',
  'gender',
  'has_optin_sms',
  'has_optin_email',
  'points',
  'checkin_count',
  'store',
  'city',
  'postal_code'
]

const STORAGE_KEY = 'customer-export-columns'

const props = withDefaults(
  defineProps<{
    path: string
    params?: Record<string, unknown>
    iconOnly?: boolean
  }>(),
  {
    params: () => ({}),
    iconOnly: false
  }
)

const { t } = useI18n()
const router = useRouter()
const notification = useNotification()

const isModalOpen = ref(false)
const inProgress = ref(false)
const selectedColumns = ref<CustomerExportColumn[]>([...DEFAULT_SELECTED_COLUMNS])

const allSelected = computed(() => selectedColumns.value.length === CUSTOMER_EXPORT_COLUMNS.length)
const noneSelected = computed(() => selectedColumns.value.length === 0)

const loadPersistedColumns = (): CustomerExportColumn[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return [...DEFAULT_SELECTED_COLUMNS]

    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return [...DEFAULT_SELECTED_COLUMNS]

    const allowed = new Set<string>(CUSTOMER_EXPORT_COLUMNS)
    const filtered = parsed.filter(
      (column): column is CustomerExportColumn => typeof column === 'string' && allowed.has(column)
    )

    return filtered.length > 0 ? filtered : [...DEFAULT_SELECTED_COLUMNS]
  } catch {
    return [...DEFAULT_SELECTED_COLUMNS]
  }
}

const persistColumns = (columns: CustomerExportColumn[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(columns))
}

const openModal = () => {
  selectedColumns.value = loadPersistedColumns()
  isModalOpen.value = true
}

const isColumnSelected = (column: CustomerExportColumn) => selectedColumns.value.includes(column)

const toggleColumn = (column: CustomerExportColumn, checked: boolean | 'indeterminate') => {
  if (checked === true) {
    if (!selectedColumns.value.includes(column)) {
      selectedColumns.value = [...selectedColumns.value, column]
    }
    return
  }

  selectedColumns.value = selectedColumns.value.filter((item) => item !== column)
}

const selectAll = () => {
  selectedColumns.value = [...CUSTOMER_EXPORT_COLUMNS]
}

const selectNone = () => {
  selectedColumns.value = []
}

const resetDefaults = () => {
  selectedColumns.value = [...DEFAULT_SELECTED_COLUMNS]
}

const orderedSelectedColumns = computed(() =>
  CUSTOMER_EXPORT_COLUMNS.filter((column) => selectedColumns.value.includes(column))
)

const confirmExport = () => {
  if (noneSelected.value || inProgress.value) return

  const columns = orderedSelectedColumns.value
  persistColumns(columns)
  isModalOpen.value = false
  inProgress.value = true

  axios
    .get(props.path, {
      params: {
        ...props.params,
        columns: columns.join(',')
      }
    })
    .then(() => {
      notification.create('success', { text: t('global.download.batch.success') })
      router.push({ name: 'ExportList' })
    })
    .catch(() => {
      notification.create('error', { text: t('global.download.error') })
    })
    .finally(() => {
      inProgress.value = false
    })
}
</script>

<template>
  <AppTooltip v-if="iconOnly" :text="t('pages.customer.customerPage.export.button')">
    <Button variant="outline" size="icon" :disabled="inProgress" @click="openModal">
      <Loader2 v-if="inProgress" class="h-4 w-4 animate-spin" />
      <Download v-else class="h-4 w-4" />
    </Button>
  </AppTooltip>
  <Button v-else variant="outline" :disabled="inProgress" @click="openModal">
    <Loader2 v-if="inProgress" class="h-4 w-4 animate-spin" />
    <Download v-else class="h-4 w-4" />
    {{ t('pages.customer.customerPage.export.button') }}
  </Button>

  <BaseModal
    v-model:is-open="isModalOpen"
    size="lg"
    :heading-title="t('pages.customer.customerPage.export.modal.title')"
    :cancel="t('global.words.cancel')"
    :link-text="t('pages.customer.customerPage.export.modal.confirm')"
    :is-disabled="noneSelected"
    :is-processing="inProgress"
    @close="isModalOpen = false"
    @click="confirmExport"
  >
    <p class="mb-4 text-sm text-foreground-secondary">
      {{ t('pages.customer.customerPage.export.modal.description') }}
    </p>

    <div class="mb-4 flex flex-wrap gap-2">
      <Button variant="outline" size="sm" :disabled="allSelected" @click="selectAll">
        {{ t('pages.customer.customerPage.export.modal.selectAll') }}
      </Button>
      <Button variant="outline" size="sm" :disabled="noneSelected" @click="selectNone">
        {{ t('pages.customer.customerPage.export.modal.selectNone') }}
      </Button>
      <Button variant="ghost" size="sm" @click="resetDefaults">
        {{ t('pages.customer.customerPage.export.modal.resetDefaults') }}
      </Button>
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label
        v-for="column in CUSTOMER_EXPORT_COLUMNS"
        :key="column"
        class="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40"
      >
        <Checkbox
          :id="`export-column-${column}`"
          :model-value="isColumnSelected(column)"
          @update:model-value="(value) => toggleColumn(column, value)"
        />
        <span>{{ t(`pages.customer.customerPage.export.columns.${column}`) }}</span>
      </label>
    </div>
  </BaseModal>
</template>
